import { Button, Card, CardBody, CardHeader, Input, Select, SelectItem, Tab, Tabs } from '@heroui/react'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form'
import { blandVariables, headerTypeOptions } from './constants'

export function HeadersSection({ direction, headers, addHeader, removeHeader, isDisabled }) {
  const { control } = useFormContext()
  const isOutbound = direction === 'outbound'
  const description = isOutbound
    ? 'Map Bland variables or static values to your custom defined SIP headers.'
    : 'Map your custom provided headers to Bland variables or static values.'

  return (
    <Card shadow="sm" radius="none" className="px-4 py-2">
      <CardHeader
        className="flex flex-row items-center justify-between pb-0"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium">Headers</h3>
        </div>
        <Button
          size="sm"
          color="primary"
          variant="flat"
          startContent={<Plus size={16} />}
          onPress={() => addHeader(direction)}
          isDisabled={isDisabled}
        >
          Add Header
        </Button>
      </CardHeader>
      <CardBody className="mt-0 pt-0">
        <div className="mb-4 text-sm text-gray-500">{description}</div>

        {headers.fields.length === 0
          ? (
              <div className="py-8 text-center text-gray-400">No headers configured</div>
            )
          : (
              <div className="space-y-4">
                {headers.fields.map((field, index) => (
                  <HeaderRow
                    key={field.id}
                    direction={direction}
                    control={control}
                    index={index}
                    isDisabled={isDisabled}
                    removeHeader={removeHeader}
                  />
                ))}
              </div>
            )}
      </CardBody>
    </Card>
  )
}

function HeaderRow({ direction, index, isDisabled, removeHeader }) {
  return (
    <div
      className="flex items-end gap-4 rounded-lg border bg-gray-50 p-4"
    >
      <HeaderRowContent direction={direction} index={index} isDisabled={isDisabled} />

      <Button
        isIconOnly
        color="danger"
        variant="light"
        onPress={() => removeHeader(direction, index)}
        isDisabled={isDisabled}
      >
        <Trash2 size={16} />
      </Button>
    </div>
  )
}

function HeaderRowContent({ direction, index, isDisabled }) {
  const { control, setValue, unregister } = useFormContext()
  const headerType = useWatch({ name: `${direction}.headers.${index}.type` })

  return (
    <>
      <Controller
        name={`${direction}.headers.${index}.type`}
        control={control}
        render={({ field }) => (
          <Select
            label="Header Type"
            placeholder="Select type"
            className="flex-1"
            isDisabled={isDisabled}
            variant="bordered"
            radius="sm"
            labelPlacement="outside"
            selectedKeys={headerType ? [headerType] : []}
            onChange={(e) => {
              field.onChange(e)
              const newType = e.target.value
              const basePath = `${direction}.headers.${index}`

              if (newType === 'uui') {
                unregister(`${basePath}.key`)
                unregister(`${basePath}.value`)
                unregister(`${basePath}.value_mode`)

                setValue(`${basePath}.content`, '')
                setValue(`${basePath}.target`, '')
                setValue(`${basePath}.purpose`, 'bland')
                setValue(`${basePath}.encoding`, 'hex')
              }
              else {
                unregister(`${basePath}.content`)
                unregister(`${basePath}.target`)
                unregister(`${basePath}.purpose`)
                unregister(`${basePath}.encoding`)

                setValue(`${basePath}.key`, '')
                setValue(`${basePath}.value`, '')
                setValue(`${basePath}.value_mode`, 'variable')
              }
            }}
          >
            {headerTypeOptions.map(option => (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        )}
      />

      {headerType === 'uui'
        ? (
            <>
              <Controller
                name={`${direction}.headers.${index}.content`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Hex Encoded Content"
                    placeholder="e.g. 7369...702d"
                    className="flex-1"
                    isDisabled={isDisabled}
                    variant="bordered"
                    radius="sm"
                    labelPlacement="outside"
                  />
                )}
              />
              <BlandVariableComponent direction={direction} index={index} isDisabled={isDisabled} propName="target" label="Target" />
            </>
          )
        : (
            <>
              <Controller
                name={`${direction}.headers.${index}.key`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Header Name"
                    placeholder="e.g. X-Custom-Header"
                    className="flex-1"
                    isDisabled={isDisabled}
                    variant="bordered"
                    radius="sm"
                    labelPlacement="outside"
                  />
                )}
              />
              <HeaderValueComponent direction={direction} index={index} isDisabled={isDisabled} />
            </>
          )}
    </>
  )
}

function HeaderValueComponent({ direction, index, isDisabled }) {
  // This component allows switching between variable and static value for the header value
  const { control, setValue } = useFormContext()
  const valueModeName = `${direction}.headers.${index}.value_mode`
  const valueName = `${direction}.headers.${index}.value`
  // Default to 'variable' if not set
  const valueMode = useWatch({ name: valueModeName }) || 'variable'

  return (
    <div className="flex-1">
      <Controller
        name={valueModeName}
        control={control}
        defaultValue="variable"
        render={({ field }) => (
          <Tabs
            selectedKey={field.value || 'variable'}
            onSelectionChange={(key) => {
              field.onChange(key)
              // Optionally clear value when switching modes
              setValue(valueName, '')
            }}
            variant="underlined"
            className="mb-2"
            isDisabled={isDisabled}
          >
            <Tab key="variable" title="Bland Variable" />
            <Tab key="static" title="Static Value" />
          </Tabs>
        )}
      />
      {valueMode === 'variable'
        ? (
            <BlandVariableComponent
              direction={direction}
              index={index}
              isDisabled={isDisabled}
              propName="value"
              label="Bland Variable"
            />
          )
        : (
            <Controller
              name={valueName}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Static Value"
                  placeholder="Enter static value"
                  className="flex-1"
                  isDisabled={isDisabled}
                  variant="bordered"
                  radius="sm"
                  labelPlacement="outside"
                />
              )}
            />
          )}
    </div>
  )
}

function BlandVariableComponent({ direction, index, isDisabled, propName, label }) {
  const { control } = useFormContext()

  const name = `${direction}.headers.${index}.${propName}`
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          label={label}
          placeholder="Select variable"
          className="flex-1"
          isDisabled={isDisabled}
          variant="bordered"
          radius="sm"
          labelPlacement="outside"
          selectedKeys={field.value ? [field.value] : []}
          onChange={field.onChange}
          onBlur={field.onBlur}
          name={field.name}
        >
          {blandVariables.map(variable => (
            <SelectItem key={variable} value={variable}>
              {variable}
            </SelectItem>
          ))}
        </Select>
      )}
    />
  )
}
